import React from 'react';
import { Mail, Phone, MapPin, Instagram, MapPinned } from 'lucide-react';

export const Footer = ({ t }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#001f3f] text-white py-16 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info with both logos */}
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-6 mb-6">
              {/* Main logo and company name */}
              <div className="flex items-center gap-6">
                <img 
                  src="/logo.png" 
                  alt="Le Phare Administratif" 
                  className="h-[250px] w-[250px] object-contain drop-shadow-2xl"
                />
                <div>
                  <h3 className="text-2xl font-bold text-[#FF9955]">Le Phare Administratif</h3>
                  <p className="text-[#FF9955] text-lg">& Gestion</p>
                </div>
              </div>
              
              {/* Services à la personne certification logo */}
              <div className="flex items-center gap-4">
                <img 
                  src="/service-personne-logo.png" 
                  alt="Services à la personne - Certification officielle" 
                  className="h-[120px] w-auto object-contain"
                />
                <div>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {t.language === 'fr' 
                      ? 'Organisme agréé Services à la personne - 50% de crédit d\'impôt' 
                      : 'Approved personal services organization - 50% tax credit'}
                  </p>
                </div>
              </div>
            </div>
            <p className="text-slate-300 mb-4 leading-relaxed">
              {t.footer.description}
            </p>
            <div className="text-sm text-slate-400 mb-4">
              <p>{t.footer.legal}</p>
            </div>
            
            {/* Social Media Links */}
            <div className="mt-6 space-y-6">
              {/* Instagram QR Code */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Instagram className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white font-semibold">
                    {t.language === 'fr' ? 'Suivez-nous sur Instagram' : 'Follow us on Instagram'}
                  </span>
                </div>
                <a 
                  href="https://www.instagram.com/lephare29200" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block hover:scale-105 transition-transform duration-300"
                >
                  <img 
                    src="/instagram-qr.png" 
                    alt="Instagram QR Code - @lephare29200" 
                    className="w-40 h-40 rounded-xl border-2 border-[#D4AF37] shadow-lg hover:shadow-2xl hover:shadow-[#D4AF37]/30"
                  />
                  <p className="text-center text-slate-400 text-sm mt-2">@lephare29200</p>
                </a>
              </div>

              {/* Google Business Profile */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <MapPinned className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white font-semibold">
                    {t.language === 'fr' ? 'Trouvez-nous sur Google' : 'Find us on Google'}
                  </span>
                </div>
                <a 
                  href="https://share.google/eCwwFDguOZfD0GbVF" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#001f3f] rounded-lg font-semibold hover:bg-[#D4AF37] hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105"
                >
                  <MapPinned className="w-5 h-5" />
                  <span>{t.language === 'fr' ? 'Voir sur Google Maps' : 'View on Google Maps'}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#D4AF37]">Liens Rapides</h4>
            <ul className="space-y-2">
              <li>
                <a href="#services" className="text-slate-300 hover:text-[#D4AF37] transition-colors">
                  {t.nav.services}
                </a>
              </li>
              <li>
                <a href="#process" className="text-slate-300 hover:text-[#D4AF37] transition-colors">
                  {t.nav.process}
                </a>
              </li>
              <li>
                <a href="#testimonials" className="text-slate-300 hover:text-[#D4AF37] transition-colors">
                  {t.nav.testimonials}
                </a>
              </li>
              <li>
                <a href="#faq" className="text-slate-300 hover:text-[#D4AF37] transition-colors">
                  {t.nav.faq}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#D4AF37]">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Phone className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <a href={`tel:${t.contact.info.phone}`} className="text-slate-300 hover:text-[#D4AF37] transition-colors">
                  {t.contact.info.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <a href={`mailto:${t.contact.info.email}`} className="text-slate-300 hover:text-[#D4AF37] transition-colors break-all">
                  {t.contact.info.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">
                  {t.contact.info.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center md:text-left">
              {t.footer.rights.replace('2025', currentYear)}
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-slate-400 hover:text-[#D4AF37] transition-colors">
                {t.footer.links.mentions}
              </a>
              <a href="#" className="text-slate-400 hover:text-[#D4AF37] transition-colors">
                {t.footer.links.privacy}
              </a>
              <a href="#" className="text-slate-400 hover:text-[#D4AF37] transition-colors">
                {t.footer.links.terms}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
